/* eslint-disable @typescript-eslint/no-unused-vars */
import { FormDataConsumer, SelectInput, TextInput, required } from 'react-admin';

import { NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { QUESTION_TYPE } from '@app/utils/questionUtils';
import { Grid, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
import { CmsAutocompleteArrayInput } from '@app/components/common/CmsAutocompleteArrayInput';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';

interface QuestionFormProps {
  title: string;
}

export const QuestionForm = ({ title }: QuestionFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDataConsumer>
        {({ formData }) => (
          <div style={{ width: '100%' }}>
            {/* width: '100%' needed to keep width of form constant */}
            <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
              {title}
            </Typography>
            <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

            <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  source="question"
                  label="Question"
                  helperText={false}
                  variant="outlined"
                  fullWidth
                  validate={[required()]}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  variant="outlined"
                  label="Description"
                  multiline
                  helperText={false}
                  rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
                  source="description"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <SelectInput
                  source="type"
                  variant="outlined"
                  helperText={false}
                  label="Question Type"
                  choices={[
                    { id: QUESTION_TYPE.SINGLE_SELECT, name: 'Single' },
                    { id: QUESTION_TYPE.FREE_TEXT, name: 'Free Text' },
                    { id: QUESTION_TYPE.MULTIPLE_SELECT, name: 'Multiple' },
                  ]}
                  fullWidth
                  validate={[required()]}
                />
              </Grid>

              {(formData.type === QUESTION_TYPE.SINGLE_SELECT || formData.type === QUESTION_TYPE.MULTIPLE_SELECT) && (
                <Grid item xs={12}>
                  <CmsAutocompleteArrayInput
                    sourceUrl="questions/responses"
                    source={`responses.${language}`}
                    label="Responses"
                    language={language}
                    validate={required()}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <LabelNotClickableBoolInput label="User Profile" source="is_user_profile" helperText={false} />
              </Grid>
              <Grid item xs={12}>
                <LabelNotClickableBoolInput
                  helperText={false}
                  label="Published"
                  source="status"
                  defaultValue={'INACTIVE'}
                  parse={(value: boolean) => {
                    if (value === true) {
                      return 'ACTIVE';
                    } else {
                      return 'INACTIVE';
                    }
                  }}
                  format={(value: string) => {
                    if (value === 'ACTIVE') {
                      return true;
                    } else {
                      return false;
                    }
                  }}
                />
              </Grid>
            </Grid>
          </div>
        )}
      </FormDataConsumer>
    </LocalizationProvider>
  );
};
